/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import cx from 'classnames'

import type { IconType } from '@elastic/eui'
import { EuiIcon, EuiLoadingSpinner, useEuiTheme } from '@elastic/eui'

import { StatusScreenReaderOnly } from './StatusScreenReaderOnly'

import type { StatusValue } from './types'

type Props = {
  status?: StatusValue | null
  iconType?: IconType
  iconShape?: string
  className?: string
  pending?: boolean
  quiet?: boolean
}

const statusWrapperStyle = css({
  display: 'inline-flex',
})

const getStatusStyle = () =>
  css({
    display: 'flex',
    alignItems: 'center',
    height: 24,
    padding: '0 0.25rem',
  })

const getStatusBackgroundStyle = ({ euiTheme }) =>
  css({
    color: euiTheme.colors.emptyShade,
    '&.status--pending': {
      backgroundColor: euiTheme.colors.vis.euiColorVis5,
      '.euiLoadingSpinner': {
        borderColor: `transparent ${euiTheme.colors.fullShade} ${euiTheme.colors.fullShade} ${euiTheme.colors.fullShade}`,
      },
    },
    '&.status--ok': {
      backgroundColor: euiTheme.colors.success,
    },
    '&.status--error': {
      backgroundColor: euiTheme.colors.danger,
    },
    '&.status--warning': {
      backgroundColor: euiTheme.colors.warning,
    },
    '&.status--stopped': {
      backgroundColor: euiTheme.colors.lightestShade,
      color: euiTheme.colors.mediumShade,
    },
    '&.status--quiet': {
      opacity: 0.1,
    },
  })

const getStatusForegroundStyle = ({ euiTheme }) =>
  css({
    '&.status--ok': {
      color: euiTheme.colors.success,
    },
    '&.status--error': {
      color: euiTheme.colors.danger,
    },
    '&.status--warning': {
      color: euiTheme.colors.warning,
    },
    '&.status--stopped': {
      color: euiTheme.colors.mediumShade,
    },
    '&.status--quiet': {
      opacity: 0.1,
    },
  })

function Status({ status, iconType, iconShape = 'default', className, pending, quiet }: Props) {
  const euiTheme = useEuiTheme()
  const isDefault = iconShape === `default`
  const isStandalone = iconShape === `standalone`
  const isDot = iconShape === `dot`

  const statusClasses = cx(
    `status`,
    {
      'status-foreground': isStandalone || isDot,
      'status-background': isDefault,
      'status--pending': pending,
      'status--ok': !pending && status === true,
      'status--error': !pending && status === false,
      'status--warning': status === `warning` || status === `maintenance`,
      'status--stopped': status === `stopped`,
      'status--quiet': quiet,
    },
    className,
  )

  const icon = getStatusIcon({ isDot, status, iconType })

  const baseStyle = getStatusStyle()
  const variantStyle = isDefault
    ? getStatusBackgroundStyle(euiTheme)
    : getStatusForegroundStyle(euiTheme)

  return (
    <span css={statusWrapperStyle}>
      <span className={statusClasses} css={[baseStyle, variantStyle]} data-test-id='statusIcon'>
        {pending ? (
          <EuiLoadingSpinner size='m' />
        ) : (
          <EuiIcon type={icon} data-test-id='status-eui-icon' />
        )}
        <StatusScreenReaderOnly status={status} pending={pending} />
      </span>
    </span>
  )
}

function getStatusIcon({
  isDot,
  status,
  iconType = `cross`,
}: {
  isDot: boolean
  status?: StatusValue | null
  iconType?: IconType
}): IconType {
  if (isDot) {
    return `dot`
  }

  if (status === `stopped`) {
    return `stopFilled`
  }

  if (status === `maintenance`) {
    return `wrench`
  }

  if (status === `deleted`) {
    return `minusCircle`
  }

  if (status === true) {
    return `check`
  }

  return iconType
}

export default Status
